import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Mail, Loader2, ArrowRight, ArrowLeft, CheckCircle2 } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.22, 1, 0.36, 1];

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const id = language === "id";
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async () => {
    if (!email.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim(), language });
      setSent(true);
    } catch {
      // even on error we show success to prevent enumeration
      setSent(true);
    }
    setLoading(false);
  };

  return (
    <div className="dark-immersive min-h-screen flex items-center justify-center px-6 py-16" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE }}
          className="glass-dark rounded-3xl p-8 sm:p-10"
        >
          <Link to="/login" className="inline-flex items-center gap-2 text-xs tracking-widest uppercase text-seikat-bone-muted hover:text-seikat-pink transition-colors mb-8" data-testid="forgot-back-login">
            <ArrowLeft size={12} /> {id ? "Kembali ke Masuk" : "Back to Login"}
          </Link>

          {sent ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, ease: EASE }}
              className="text-center"
              data-testid="forgot-sent"
            >
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-seikat-lime/15 flex items-center justify-center">
                <CheckCircle2 size={28} className="text-seikat-lime" strokeWidth={1.4} />
              </div>
              <p className="eyebrow-immersive text-seikat-lime">{id ? "Tautan Terkirim" : "Link Sent"}</p>
              <h1 className="mt-3 font-heading text-3xl text-seikat-bone leading-tight">
                {id ? <>Periksa <em>inbox</em>-mu</> : <>Check your <em>inbox</em></>}
              </h1>
              <p className="mt-4 font-body text-seikat-bone/65 text-sm leading-relaxed">
                {id
                  ? `Jika akun dengan email "${email}" ada, kami sudah mengirim tautan untuk atur ulang kata sandi. Berlaku 1 jam.`
                  : `If an account with "${email}" exists, we've sent a password reset link. Valid for 1 hour.`}
              </p>
              <button onClick={() => navigate("/login")} className="btn-pill btn-pill--solid mt-8 w-full justify-center" data-testid="forgot-back-to-login">
                {id ? "Kembali ke Masuk" : "Back to Sign In"} <ArrowRight size={14} />
              </button>
            </motion.div>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-4">
                <span className="w-8 h-px bg-seikat-pink" />
                <span className="eyebrow-immersive">{id ? "Atur Ulang" : "Reset"}</span>
              </div>
              <h1 className="font-heading text-4xl text-seikat-bone leading-[0.95]">
                {id ? <>Lupa <em>kata sandi</em>?</> : <>Forgot your <em>password</em>?</>}
              </h1>
              <p className="mt-4 font-body text-seikat-bone/65 text-sm leading-relaxed">
                {id
                  ? "Masukkan email akunmu. Kami akan kirim tautan untuk membuat kata sandi baru."
                  : "Enter your account email. We will send a link to create a new password."}
              </p>
              <div className="mt-8 space-y-4">
                <label className="block">
                  <div className="flex items-center gap-1.5 mb-2 font-body text-[10px] tracking-[0.25em] uppercase text-seikat-bone-muted">
                    <Mail size={11} strokeWidth={1.6} /> Email
                  </div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
                    placeholder="you@email.com"
                    className="immersive-input"
                    data-testid="forgot-email-input"
                    autoFocus
                  />
                </label>
                <button
                  onClick={handleSubmit}
                  disabled={loading || !email.trim()}
                  className="btn-pill btn-pill--solid w-full justify-center mt-3 disabled:opacity-50"
                  data-testid="forgot-submit-btn"
                  data-magnet
                >
                  {loading ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
                  {id ? "Kirim Tautan Reset" : "Send Reset Link"}
                </button>
              </div>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
